import Dropdown from "common/Dropdown/Dropdown";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import EllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import PlayCircleOutlined from "@ant-design/icons/PlayCircleOutlined";
import RedoOutlined from "@ant-design/icons/RedoOutlined";
import React, { CSSProperties, useContext } from "react";
import ThunderboltOutlined from "@ant-design/icons/ThunderboltOutlined";
import {
  CardsSelectorContext,
  ContextProps,
} from "@shared/common/CardsList/CardsSelector.context";
import { getSignalExtraDetailsRoute } from "utils/routes";
import { SIGNAL_EXTRA_DETAILS_TABS } from "@shared/common/constants";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { Signal, LanguageNameSpaces } from "@shared/types";
import { FollowSignalType } from "@shared/features/signals/common/types";
import { SignalFollowActionButton } from "features/signals/SignalFollow/SignalFollowActionButton";
import { ItemType } from "antd/es/menu/interface";
import styled, { css, useTheme } from "styled-components";
import { useSignalAdminActions } from "features/signals/common/hooks.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useDisplayTradeDetails } from "@shared/utils/hooks/misc.ts";
import { SignalActionsContext } from "@shared/features/signals/common/SignalActions.context";
import NewLookKebab from "features/portfolio/NewLook/NewLookKebab.tsx";

interface SignalCardActionsProps {
  signal: Signal;
}

const SignalCardActions = ({ signal }: SignalCardActionsProps) => {
  const { showEditSignalActions } = useContext(SignalActionsContext) || {};
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { historyPush } = useHistoryWithQuery();
  const theme = useTheme();
  const { menuItems: adminMenuItems, cancelSignalModal } =
    useSignalAdminActions(signal.signal_id);
  const displayTradeDetails = useDisplayTradeDetails();
  const { setIsEditMode } =
    useContext<ContextProps<Signal> | undefined>(CardsSelectorContext) || {};

  let menuItems: ItemType[] = [];

  if (displayTradeDetails) {
    menuItems.push({
      key: "signalDetails",
      icon: <InfoCircleOutlined />,
      label: t("drawer.signal_details"),
      onClick: () =>
        historyPush(
          getSignalExtraDetailsRoute(
            location.pathname,
            signal.signal_id,
            SIGNAL_EXTRA_DETAILS_TABS.GENERAL,
            false
          )
        ),
    });
  }

  const actionButtonProps = {
    signal,
    buttonStyle: {
      width: "100%",
      textAlign: "left",
    } as CSSProperties,
    isCard: true,
    color: theme.colors.primaryGrey,
  };

  if (signal.is_open) {
    menuItems.push(
      {
        key: "follow",
        label: (
          <SignalFollowActionButton
            followType={FollowSignalType.FOLLOW}
            icon={<StyledPlayCircleOutlined />}
            {...actionButtonProps}
          />
        ),
      },
      {
        key: "update",
        label: (
          <SignalFollowActionButton
            followType={FollowSignalType.UPDATE}
            icon={<StyledRedoOutlined />}
            {...actionButtonProps}
          />
        ),
      }
    );
  } else {
    menuItems.push({
      key: "close",
      label: (
        <SignalFollowActionButton
          followType={FollowSignalType.CANCEL}
          icon={<StyledDeleteOutlined />}
          {...actionButtonProps}
        />
      ),
    });
  }

  if (showEditSignalActions) {
    menuItems = [
      ...adminMenuItems,
      ...menuItems,
      {
        key: "bulkActions",
        icon: <ThunderboltOutlined />,
        label: t("actions.bulk_actions"),
        onClick: () => setIsEditMode?.(signal),
      },
    ];
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown
        title={t("actions.signal_actions")}
        menu={{ items: menuItems }}
        trigger={["click"]}
      >
        {theme.isNewLook ? (
          <NewLookKebab aria-label={t("actions.signal_actions")} />
        ) : (
          <StyledEllipsisOutlined />
        )}
      </Dropdown>
      {showEditSignalActions && cancelSignalModal}
    </div>
  );
};

export default SignalCardActions;

const iconStyle = css`
  font-size: 14px;
  &.anticon svg {
    color: ${(p) => p.theme.colors.primaryGrey};
  }
`;
const StyledRedoOutlined = styled(RedoOutlined)`
  transform: rotate(180deg);
  ${iconStyle}
`;
const StyledPlayCircleOutlined = styled(PlayCircleOutlined)`
  ${iconStyle}
`;
const StyledDeleteOutlined = styled(DeleteOutlined)`
  ${iconStyle}
`;
const StyledEllipsisOutlined = styled(EllipsisOutlined)`
  cursor: pointer;
  font-size: 30px;
  transform: rotate(90deg);
  color: ${(p) => p.theme.colors.primaryGrey};
`;
